import { lazy, Suspense, useEffect, useState } from 'react'
import { createBrowserRouter, Navigate, Link, Outlet, useLocation, useNavigate, useRouteError } from 'react-router-dom'
import { Sentry, sessionId } from '../sentry'
import { isChunkError } from '../sentry-beforesend'
import { AuthGuard } from './AuthGuard'
import { SessionExpiredModal } from '../components/SessionExpiredModal'
import { useAppDispatch, useAppSelector } from '../store'
import {
  markLoginRedirectHandled,
  selectShouldRedirectToLogin,
} from '../store/slices/sessionExpirationSlice'

/**
 * RouteErrorBoundary — единственный chunk-error reload handler в приложении.
 *
 * React Router c createBrowserRouter перехватывает ошибки на route level раньше
 * React error boundaries — поэтому отдельный <ChunkLoadErrorBoundary> в App.tsx
 * не имел смысла (никогда не срабатывал для route-level lazy()) и был удалён.
 *
 * Логика: если это ChunkLoadError — reload один раз с TTL guard.
 * sessionStorage хранит timestamp последней попытки. Если ошибка повторяется
 * в течение CHUNK_RELOAD_TTL_MS (10 сек) — это значит reload не помог
 * (битый deploy, nginx удалил старые ассеты): показываем generic error UI
 * с кнопкой "Обновить" вместо infinite reload loop. После TTL — снова можем
 * попробовать reload (например если в той же session случился второй deploy).
 */
const CHUNK_RELOAD_KEY = 'pg19_chunk_reload_at'
const CHUNK_RELOAD_TTL_MS = 10_000

function RouteErrorBoundary() {
  const error = useRouteError() as Error | undefined
  const chunk = isChunkError(error)

  let recentlyTried = false
  if (typeof sessionStorage !== 'undefined') {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY)
    const at = raw ? Number(raw) : 0
    recentlyTried = at > 0 && Date.now() - at < CHUNK_RELOAD_TTL_MS
  }
  const shouldReload = chunk && !recentlyTried

  useEffect(() => {
    if (shouldReload) {
      console.warn('[RouteErrorBoundary] chunk load error after deploy, auto-reloading once')
      try {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
      } catch {}
      const t = setTimeout(() => window.location.reload(), 500)
      return () => clearTimeout(t)
    }
    // Не chunk error — это настоящая проблема route/loader. Шлём в Sentry чтобы
    // увидеть. Chunk errors отфильтрованы в beforeSend (двойная защита).
    if (error && !chunk) {
      Sentry.captureException(error)
    }
  }, [shouldReload, chunk, error])

  if (shouldReload) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#F7941D] border-t-transparent mx-auto mb-3" />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Обновляем приложение…</p>
        </div>
      </div>
    )
  }

  // Не наша область — показываем generic error UI + session_id для support.
  return <NonChunkErrorFallback error={error} />
}

/**
 * v2: fallback UI с session_id для client-to-support communication.
 * Клиент читает 8-char код, диктует/копирует саппорту. Support UI имеет tab
 * "По сессии" — находит events с tags.session_id без user.id (pre-auth errors,
 * logout race, SDK-didn't-load cases).
 */
function NonChunkErrorFallback({ error }: { error: Error | undefined }) {
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sessionId)
      setCopied('ok')
    } catch {
      setCopied('fail')
    }
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <h1 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Что-то пошло не так</h1>
      <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>{error?.message || 'Неизвестная ошибка'}</p>

      <div className="mb-4 w-full max-w-sm">
        <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
          Если повторится, сообщите саппорту код сессии:
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2 rounded border text-base font-mono text-center" style={{ borderColor: 'var(--border-color, #e2e8f0)', background: 'var(--bg-muted, #f1f5f9)' }}>
            {sessionId}
          </code>
          <button onClick={handleCopy} className="btn btn-secondary btn-sm" style={{ minWidth: 90 }}>
            {copied === 'ok' ? 'Скопировано' : copied === 'fail' ? 'Ошибка' : 'Копировать'}
          </button>
        </div>
      </div>

      <button onClick={() => window.location.reload()} className="btn btn-primary btn-sm">Обновить страницу</button>
    </div>
  )
}

// Lazy-loaded pages (code splitting)
const LoginPage = lazy(() => import('../pages/LoginPage'))
const ContractPage = lazy(() => import('../pages/ContractPage'))
const DashboardPage = lazy(() => import('../pages/DashboardPage'))
const ProfilePage = lazy(() => import('../pages/ProfilePage'))
const InvoicesPage = lazy(() => import('../pages/InvoicesPage'))
const AutopayPage = lazy(() => import('../pages/AutopayPage'))
const NewsPage = lazy(() => import('../pages/NewsPage'))
const PaymentPage = lazy(() => import('../pages/PaymentPage'))
const SupportPage = lazy(() => import('../pages/SupportPage'))
const TicketPage = lazy(() => import('../pages/TicketPage'))
const RegisterRedirectPage = lazy(() => import('../pages/RegisterRedirectPage'))

// Layout import (not lazy — always needed)
import { DefaultLayout } from '../layouts/DefaultLayout'
import { SeniorProfileGuard } from './SeniorProfileGuard'

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#F7941D] border-t-transparent" />
      </div>
    }>
      {children}
    </Suspense>
  )
}

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

/**
 * RootLayout — корневой обёрточный layout.
 *
 * Монтирует <SessionExpiredModal/> один раз для всего приложения. Модалка
 * появляется поверх любой страницы (public `/login` или protected `/dashboard`)
 * при AUTH_EXPIRED от RTK Query. Invariant isAuthenticated внутри модалки
 * гарантирует что на `/login` она не покажется.
 *
 * Outlet рендерит все дочерние роуты — существующая логика не меняется.
 */
function RootLayout() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const shouldRedirectToLogin = useAppSelector(selectShouldRedirectToLogin)

  useEffect(() => {
    if (!shouldRedirectToLogin) return

    if (location.pathname !== '/login') {
      navigate('/login', { replace: true })
    }
    dispatch(markLoginRedirectHandled())
  }, [dispatch, location.pathname, navigate, shouldRedirectToLogin])

  return (
    <>
      <SessionExpiredModal />
      <Outlet />
    </>
  )
}

export const router = sentryCreateBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Public routes
      {
        path: '/login',
        element: <SuspenseWrapper><LoginPage /></SuspenseWrapper>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/login/register',
        element: <SuspenseWrapper><RegisterRedirectPage /></SuspenseWrapper>,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/contract',
        element: <SuspenseWrapper><ContractPage /></SuspenseWrapper>,
        errorElement: <RouteErrorBoundary />,
      },

      // Protected routes
      {
        element: <AuthGuard />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            element: <DefaultLayout />,
            errorElement: <RouteErrorBoundary />,
            children: [
              {
                path: '/dashboard',
                element: <SuspenseWrapper><DashboardPage /></SuspenseWrapper>,
              },
              {
                path: '/profile',
                element: <SeniorProfileGuard />,
                children: [{
                  index: true,
                  element: <SuspenseWrapper><ProfilePage /></SuspenseWrapper>,
                }],
              },
              {
                path: '/invoices',
                element: <SuspenseWrapper><InvoicesPage /></SuspenseWrapper>,
              },
              {
                path: '/invoices/:id/payment',
                element: <SuspenseWrapper><PaymentPage /></SuspenseWrapper>,
              },
              {
                path: '/autopay',
                element: <SuspenseWrapper><AutopayPage /></SuspenseWrapper>,
              },
              {
                path: '/news',
                element: <SuspenseWrapper><NewsPage /></SuspenseWrapper>,
              },
              {
                path: '/support',
                element: <SuspenseWrapper><SupportPage /></SuspenseWrapper>,
              },
              {
                path: '/support/:id',
                element: <SuspenseWrapper><TicketPage /></SuspenseWrapper>,
              },
            ],
          },
        ],
      },

      // Root redirect
      {
        path: '/',
        element: <Navigate to="/dashboard" replace />,
      },

      // 404 fallback
      {
        path: '*',
        element: (
          <div className="min-h-screen mesh-gradient-hero flex flex-col items-center justify-center px-4">
            <h1 className="text-6xl font-bold text-[#F7941D] mb-4">404</h1>
            <p className="text-xl mb-6" style={{ color: 'var(--text-primary)' }}>Страница не найдена</p>
            <Link to="/dashboard" className="btn btn-primary btn-sm">На главную</Link>
          </div>
        ),
      },
    ],
  },
])
